<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">PLS扫码入库</h1>
    </header>

    <div v-if="statusMsg" class="status-banner" :class="statusType">
      {{ statusMsg }}
    </div>

    <main class="content">
      <section class="card">
        <input
          ref="plsInput"
          v-model="receiptNo"
          class="input"
          type="text"
          autocomplete="off"
          placeholder="请扫描PLS单号"
          @input="handleInputChange"
          @keyup.enter="handleQuery"
        />

        <div class="actions">
          <button class="btn primary" :disabled="loading" @click="handleQuery">
            {{ loading ? '查询中...' : '查询' }}
          </button>
          <button class="btn danger" :disabled="loading" @click="handleClear">
            清空
          </button>
        </div>
      </section>

      <section class="card" v-if="records.length">
        <div class="list-header">
          <div class="list-title">查询结果如下</div>
        </div>

        <div class="list">
          <div
            v-for="item in records"
            :key="String(item.wareInId)"
            class="result-card"
            :class="{
              selected: selectedId === String(item.wareInId),
              disabled: item.wareInStatus !== 0 || !item.wareInId
            }"
            @click="selectCard(item)"
          >
            <div class="result-card__head">
              <div class="result-card__title">{{ item.receiptNo || '-' }}</div>
              <div class="result-card__status" :class="statusClass(item)">
                {{ item.wareInStatus === 0 ? '待分配' : item.wareInStatus === 6 ? '正在入库中' : item.wareInStatus === 3 ? '已完成' : '异常' }}
              </div>
            </div>

            <div class="result-card__body">
              <div class="kv">
                <div class="k">供应商</div>
                <div class="v">{{ item.supplierName || '-' }}</div>
              </div>
              <div class="kv">
                <div class="k">主机厂</div>
                <div class="v">{{ item.factoryName || '-' }}</div>
              </div>

              <div class="material-row">
                <div class="material-line">
                  <span class="m-label">编码</span>
                  <span class="m-value material-no">{{ item.materialNo || '-' }}</span>
                </div>
                <div class="material-line">
                  <span class="m-label">描述</span>
                  <span class="m-value material-desc">{{ item.materialDesc || '-' }}</span>
                </div>
                <div class="material-line">
                  <span class="m-label">数量</span>
                  <span class="m-value material-qty">
                    {{ item.wareInNum ? Number(item.wareInNum).toFixed(3) : '0.000' }}
                    <span class="unit">{{ item.packageUnitName || '' }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="actions">
          <button class="btn primary" :disabled="loading || !canSubmit" @click="handleSubmit">
            {{ loading ? '入库中...' : '确认入库' }}
          </button>
        </div>
      </section>

      <section v-else class="empty">
        请扫码查询
      </section>
    </main>

    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">{{ loadingText }}</div>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'PLSScanStockIn',
  data() {
    return {
      audioContext: null,
      isAudioContextActive: false,

      receiptNo: '',
      parseTimer: null,

      records: [],
      selectedId: '',

      statusMsg: '',
      statusType: '',
      loading: false,
      loadingText: '加载中...'
    }
  },
  computed: {
    canSubmit() {
      return !!this.selectedId
    }
  },
  mounted() {
    document.addEventListener('click', this.initAudioContext, { once: true })
    document.addEventListener('touchstart', this.initAudioContext, { once: true })

    this.$nextTick(() => {
      if (this.$refs.plsInput) {
        this.$refs.plsInput.focus()
      }
    })
  },
  beforeUnmount() {
    clearTimeout(this.parseTimer)
  },
  methods: {
    handleBack() {
      this.$router.go(-1)
    },

    handleInputChange() {
      this.receiptNo = (this.receiptNo || '').trim()

      if (!this.receiptNo) {
        clearTimeout(this.parseTimer)
        this.records = []
        this.selectedId = ''
        return
      }

      clearTimeout(this.parseTimer)
      this.parseTimer = setTimeout(() => {
        this.handleQuery()
      }, 300)
    },

    async handleQuery() {
      const no = (this.receiptNo || '').trim()
      if (!no) {
        this.showStatus('请扫描单号', 'error')
        return
      }
      if (this.loading) return

      this.loading = true
      this.loadingText = '正在查询...'
      this.showStatus('正在查询...', 'info')

      try {
        const resp = await request.get('/uic-system/wareindetail/pls/page', {
          params: {
            source: 1,
            receiptNo: no,
            current: 1,
            size: 5
          },
          timeout: 30000
        })

        const list = resp && resp.data && Array.isArray(resp.data.records) ? resp.data.records : []
        this.records = list

        const firstSelectable = list.find(r => r && r.wareInStatus === 0 && r.wareInId != null)
        this.selectedId = firstSelectable ? String(firstSelectable.wareInId) : ''

        if (this.records.length) {
          this.showStatus(`查询成功：${this.records.length}条`, 'success')
          this.playSuccessBeep()
        } else {
          this.showStatus('未查询到数据', 'info')
        }
      } catch (e) {
        this.records = []
        this.selectedId = ''
        this.showStatus(`查询失败: ${e && e.message ? e.message : '网络异常'}`, 'error')
      } finally {
        this.loading = false
      }
    },

    selectCard(item) {
      if (!item || item.wareInStatus !== 0 || item.wareInId == null) return
      this.selectedId = String(item.wareInId)
    },

    statusClass(item) {
      const s = item && item.wareInStatus
      if (s === 0) return 'pending'
      if (s === 3) return 'done'
      if (s === 6) return 'doing'
      return 'error'
    },

    async handleSubmit() {
      if (this.loading) return
      if (!this.canSubmit) {
        this.showStatus('请选择可入库的记录（待分配）', 'error')
        return
      }

      const ok = window.confirm('确认入库？')
      if (!ok) return

      this.loading = true
      this.loadingText = '正在入库...'
      this.showStatus('正在入库...', 'info')

      try {
        const ids = this.selectedId
        const resp = await request.post('/uic-system/warein/pls/batch/in', { ids }, { timeout: 30000 })

        if (resp && resp.code === 200 && resp.success) {
          this.showStatus('入库成功', 'success')
          this.playSuccessBeep()
          this.handleClear(false)
        } else {
          this.showStatus(`入库失败: ${(resp && resp.msg) || '未知错误'}`, 'error')
        }
      } catch (e) {
        this.showStatus(`入库失败: ${e && e.message ? e.message : '网络异常'}`, 'error')
      } finally {
        this.loading = false
      }
    },

    handleClear(refocus = true) {
      this.receiptNo = ''
      this.records = []
      this.selectedId = ''
      this.statusMsg = ''
      this.statusType = ''
      clearTimeout(this.parseTimer)

      if (refocus) {
        this.$nextTick(() => {
          if (this.$refs.plsInput) {
            this.$refs.plsInput.focus()
          }
        })
      }
    },

    // 初始化并激活 AudioContext（保留原页面“扫码成功声音”逻辑）
    initAudioContext() {
      if (this.audioContext || this.isAudioContextActive) return

      try {
        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
        this.audioContext = new AudioContextConstructor()

        if (this.audioContext.state === 'suspended') {
          this.audioContext
            .resume()
            .then(() => {
              this.isAudioContextActive = true
            })
            .catch(() => {})
        } else {
          this.isAudioContextActive = true
        }

        document.removeEventListener('click', this.initAudioContext)
        document.removeEventListener('touchstart', this.initAudioContext)
      } catch (err) {
        console.warn('创建 AudioContext 失败：', err)
      }
    },

    // 成功提示音（保留原页面逻辑）
    playSuccessBeep() {
      try {
        if (!this.audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          this.audioContext = new AudioContextConstructor()
        }

        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().catch(() => {})
        }

        const oscillator = this.audioContext.createOscillator()
        const gainNode = this.audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(this.audioContext.destination)

        oscillator.frequency.setValueAtTime(3000, this.audioContext.currentTime)
        gainNode.gain.setValueAtTime(1, this.audioContext.currentTime)

        oscillator.start()
        oscillator.stop(this.audioContext.currentTime + 0.1)
      } catch (err) {
        console.warn('播放成功提示音失败：', err)
      }
    },

    showStatus(msg, type = 'info') {
      this.statusMsg = msg
      this.statusType = type

      setTimeout(() => {
        this.statusMsg = ''
        this.statusType = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
/* 基础重置与移动端适配 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  touch-action: manipulation; /* 优化移动端触摸交互 */
  -webkit-tap-highlight-color: transparent; /* 去除移动端点击高亮 */
}

 html {
  font-size: 16px;
  /* 响应式字体适配 */
  @media (max-width: 375px) {
    font-size: 14px;
  }
  @media (max-width: 320px) {
    font-size: 12px;
  }
 }
 
 .page,
.pls-scan-stock-in-container {
  min-height: 100vh;
  background-color: var(--background-color);
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

 .card {
  background-color: var(--card-background);
  border-radius: 0.625rem;
  padding: 1rem;
  margin-bottom: 0.875rem;
  box-shadow: 0 2px 12px var(--shadow-light);
}

 .input {
  width: 100%;
  padding: 0.875rem 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: 0.625rem;
  font-size: 16px;
  line-height: 1.2;
  color: var(--text-primary);
  background: var(--card-background);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

 .input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(35, 177, 192, 0.12);
  background: var(--card-background);
}

 .actions {
  display: flex;
  gap: 0.625rem;
  margin-top: 0.875rem;
 }

 .btn {
  flex: 1;
  border: none;
  border-radius: 0.625rem;
  min-height: 2.75rem;
  padding: 0.75rem 0.875rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
 }

 .btn:active:not(:disabled) {
  transform: scale(0.99);
 }

 .btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
 }

 .btn.primary {
  background-color: var(--primary-color);
  color: #fff;
}

 .btn.primary:not(:disabled) {
  box-shadow: 0 4px 12px rgba(35, 177, 192, 0.18);
 }

 .btn.danger {
  background-color: #ff4d4f;
  color: #fff;
 }

 .list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 0.75rem;
}

.list-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-primary);
}

.list-sub {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  flex: 0 0 auto;
}

 .list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
 }

 .result-card {
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  background: var(--card-background);
  overflow: hidden;
  box-shadow: 0 1px 10px var(--shadow-light);
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

 .result-card:active {
  transform: scale(0.995);
 }

 .result-card.selected {
  border-color: var(--primary-color);
  box-shadow: 0 6px 18px rgba(35, 177, 192, 0.16);
}

 .result-card.disabled {
  opacity: 0.55;
 }

 .result-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 0.875rem;
  background: linear-gradient(180deg, rgba(35, 177, 192, 0.08), rgba(35, 177, 192, 0.02));
 }

 .result-card__title {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: 0.2px;
}

 .result-card__status {
  flex: 0 0 auto;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
 }

 .result-card__status.pending {
  background: #e6fffb;
  color: #08979c;
 }

 .result-card__status.doing {
  background: #fff7e6;
  color: #fa8c16;
 }

 .result-card__status.done {
  background: #f6ffed;
  color: #52c41a;
 }

 .result-card__status.error {
  background: #fff1f0;
  color: #ff4d4f;
 }

 .result-card__body {
  padding: 0.875rem;
 }

 .kv {
  display: flex;
  gap: 0.5rem;
  line-height: 1.35;
  margin-top: 0.375rem;
 }

 .kv:first-child {
  margin-top: 0;
 }

 .kv .k {
  color: var(--text-secondary);
  flex: 0 0 3rem;
}

.kv .v {
  color: var(--text-primary);
  font-weight: 600;
  flex: 1;
  word-break: break-word;
}

 .material-row {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.625rem;
  background: var(--background-color);
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

 .material-line {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  line-height: 1.35;
 }

 .m-label {
  flex: 0 0 2.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--text-secondary);
}

 .m-value {
  flex: 1;
  min-width: 0;
 }

 .material-no {
  font-weight: 900;
  color: var(--text-primary);
  font-size: 0.9375rem;
}

.material-desc {
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.875rem;
  word-break: break-word;
}

.material-qty {
  color: var(--primary-color);
  font-weight: 900;
  font-size: 0.9375rem;
  white-space: nowrap;
}

 .material-qty .unit {
  margin-left: 0.25rem;
  color: var(--text-secondary);
  font-weight: 700;
  font-size: 0.75rem;
}

 .label {
  color: var(--text-secondary);
  flex: 0 0 auto;
}

.value {
  color: var(--text-primary);
  font-weight: 600;
}

 .empty {
  color: var(--text-secondary);
  text-align: center;
  padding: 2rem 0 1rem;
  font-size: 0.9375rem;
}

 .loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2000;
 }

 .loading-spinner {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  border: 3px solid rgba(255, 255, 255, 0.55);
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
 }

 .loading-text {
  margin-top: 0.875rem;
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
 }

 @keyframes spin {
  to {
    transform: rotate(360deg);
  }
 }

/* 头部样式 - 移动端优化 */
.header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 4rem; /* 64px，适配移动端 */
}

.back-btn {
  background: none;
  border: none;
  padding: 0.3125rem;
  cursor: pointer;
  margin-right: 1rem;
  border-radius: 0.25rem;
  transition: background-color 0.3s ease;
}

.back-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.icon-back {
  width: 1.5rem; /* 24px */
  height: 1.5rem; /* 24px */
  object-fit: contain;
}

.title {
  font-size: 1.125rem; /* 18px */
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

/* 内容区域 - 移动端适配 */
.content {
  flex: 1;
  padding: 1.25rem;
  margin-top: 4rem; /* 与头部高度匹配 */
  overflow-y: auto;
  min-height: calc(100vh - 4rem);
  -webkit-overflow-scrolling: touch; /* 移动端顺滑滚动 */
}

/* Tab切换样式 */
.tab-section {
  display: flex;
  background-color: var(--card-background);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 12px var(--shadow-light);
}

.tab-item {
  flex: 1;
  padding: 0.875rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.tab-item:hover {
  background-color: var(--background-color);
}

.tab-item.active {
  background-color: var(--primary-color);
  color: white;
  font-weight: 600;
}

/* 表单区域 - 视觉优化 */
.form-section {
  background-color: var(--card-background);
  border-radius: 0.5rem; /* 圆角优化 */
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 12px var(--shadow-light); /* 柔和阴影 */
  transition: box-shadow 0.3s ease;
}

.form-section:hover {
  box-shadow: 0 4px 16px var(--shadow-light);
}

.section-title {
  font-size: 1rem; /* 16px */
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color); /* 更柔和的分割线 */
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 3rem;
  height: 2px;
  background-color: var(--primary-color); /* 主题色下划线 */
}

/* 表单组 - 移动端适配 */
.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem; /* 14px */
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-color); /* 更浅的边框 */
  border-radius: 0.5rem; /* 圆角优化 */
  font-size: 0.875rem; /* 14px */
  color: var(--text-primary);
  background: var(--card-background);
  box-sizing: border-box;
  transition: all 0.3s ease;
  -webkit-appearance: none; /* 去除移动端默认样式 */
  appearance: none;
}

/* iOS输入框适配：避免自动缩放 */
.form-group input {
  font-size: 16px !important;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--primary-color); /* 主题色聚焦 */
  box-shadow: 0 0 0 3px rgba(35, 177, 192, 0.1); /* 柔和聚焦阴影 */
}

/* PLS输入框样式优化 */
.pls-input {
  font-size: 0.875rem;
  font-family: monospace;
  background-color: var(--card-background);
  border: 1px solid var(--border-color);
}

/* 按钮样式 - 移动端优化 */
.btn {
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 0.5rem; /* 圆角优化 */
  font-size: 0.875rem; /* 14px */
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 0.5rem;
  min-height: 2.5rem; /* 按钮最小高度，适配触摸 */
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.btn:last-child {
  margin-right: 0;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* 按钮细化样式 */
.parse-btn {
  background-color: var(--primary-color); /* 主题色 */
  color: #fff;
}

.parse-btn:hover:not(:disabled) {
  background-color: rgba(35, 177, 192, 0.8);
  transform: translateY(-1px);
}

.clear-btn {
  background-color: #ff4d4f;
  color: #fff;
}

.clear-btn:hover:not(:disabled) {
  background-color: #ff7875;
  transform: translateY(-1px);
}

.submit-btn {
  background-color: #23b1c0;
  color: #fff;
  padding: 0.75rem 40px;
  font-size: 1rem; /* 16px */
  font-weight: 600;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%; /* 移动端全屏宽 */
}

.submit-btn:hover:not(:disabled) {
  background-color: #1ea5b3;
  box-shadow: 0 4px 12px rgba(35, 177, 192, 0.2);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
  background-color: #d9d9d9;
  color: #bfbfbf;
  transform: none !important;
  box-shadow: none;
}

/* 按钮容器 - 移动端适配 */
.scan-actions,
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

/* 状态提示 - 悬浮提示效果 */
.status-banner {
  position: fixed;
  top: 4.5rem; /* 距离顶部，考虑到header的高度 */
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  margin: 0;
  font-size: 0.875rem; /* 14px */
  text-align: center;
  transition: all 0.3s ease;
  z-index: 999;
  min-width: 12.5rem;
  max-width: 80%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.status-banner.info {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #b3d8ff;
}

.status-banner.success {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #d9f7be;
}

.status-banner.error {
  background-color: #fff1f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

/* 数据卡片 - 移动端优化 */
.external-data-card {
  background-color: #fafafa;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid #f0f0f0;
}

.card-row {
  display: flex;
  flex-wrap: wrap; /* 移动端换行 */
  align-items: flex-start;
  margin-bottom: 0.75rem;
  font-size: 0.875rem; /* 14px */
}

.card-row:last-child {
  margin-bottom: 0;
}

.row-label {
  font-weight: 500;
  color: #666;
  min-width: 5rem; /* 标签最小宽度 */
  flex: 0 0 5rem;
  margin-right: 0.5rem;
}

.row-value {
  color: #333;
  flex: 1;
  word-break: break-all; /* 移动端文字换行 */
}

.row-value.text-primary {
  color: #23b1c0; /* 主题色 */
  font-weight: 600;
}

/* 空状态 - 视觉优化 */
.empty-state {
  text-align: center;
  padding: 3.75rem 1.25rem;
  color: #999;
}

.empty-icon {
  font-size: 4rem; /* 64px */
  margin-bottom: 1rem;
  opacity: 0.8;
}

.empty-text {
  font-size: 1rem; /* 16px */
  line-height: 1.5;
}

/* 弹窗样式 - 移动端适配 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start; /* 移动端弹窗靠上 */
  z-index: 1000;
  padding: 1.25rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal-container {
  background-color: #fff;
  border-radius: 0.75rem; /* 更大圆角 */
  width: 100%;
  max-width: 28rem; /* 500px */
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-top: 2rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f5f5f5;
}

.modal-title {
  font-size: 1rem; /* 16px */
  font-weight: 600;
  color: #333;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem; /* 24px */
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 1.875rem; /* 30px */
  height: 1.875rem; /* 30px */
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%; /* 圆形关闭按钮 */
  transition: all 0.3s ease;
}

.modal-close:hover {
  background-color: #f5f5f5;
  color: #666;
}

.modal-body {
  padding: 1.25rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.25rem;
  border-top: 1px solid #f5f5f5;
  gap: 0.5rem;
}

.modal-cancel {
  background-color: #f5f5f5;
  color: #666;
  flex: 1;
}

.modal-cancel:hover {
  background-color: #e6e6e6;
}

.modal-confirm {
  background-color: #23b1c0;
  color: #fff;
  flex: 1;
}

.modal-confirm:hover:not(:disabled) {
  background-color: #1ea5b3;
}

/* 必填字段标识 */
.required {
  color: #ff4d4f;
  margin-left: 0.125rem;
}

/* 加载遮罩层 - 视觉优化 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.loading-spinner {
  width: 3.125rem; /* 50px */
  height: 3.125rem; /* 50px */
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #23b1c0; /* 主题色加载动画 */
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.loading-text {
  color: white;
  font-size: 1rem; /* 16px */
  font-weight: 500;
  text-align: center;
  padding: 0 1rem;
}

/* 动画优化 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 媒体查询 - 小屏适配 */
@media (max-width: 375px) {
  .content {
    padding: 0.75rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .modal-container {
    margin-top: 1rem;
  }
  
  .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem; /* 13px */
  }
}

/* 文本颜色样式 */
.text-error {
  color: #ff4d4f;
}

.text-success {
  color: #52c41a;
}

/* 入库失败记录样式 */
.failed-records-section {
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.failed-records-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.failed-record-item {
  border: 1px solid #e8e8e8;
  border-radius: 0.5rem;
  padding: 1rem;
  transition: all 0.3s ease;
}

.failed-record-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #23b1c0;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.875rem;
}

.record-title {
  font-weight: 600;
  color: #333;
  font-size: 0.9375rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.pending {
  background-color: #fff7e6;
  color: #fa8c16;
}

.record-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.record-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.record-info .label {
  color: #666;
  font-size: 0.875rem;
  margin-right: 0.5rem;
  min-width: 6.25rem;
}

.record-info .value {
  color: #333;
  font-size: 0.875rem;
  flex: 1;
}

.record-info.fail-reason {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e8e8e8;
}

.record-info.fail-reason .label {
  color: #f5222d;
}

.record-info.fail-reason .value {
  color: #f5222d;
  font-weight: 500;
  word-break: break-word;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  color: #666;
}

</style>